/**
 * Eating system. Uses the spatial hash for broad-phase, then the precise
 * canEat() test. Each surviving-bigger entity may consume overlapping smaller
 * entities; eaten entities are flagged dead and the attacker grows by the
 * converted mass. Returns the eat events for scoring / effects / death checks.
 */
import type { GameConfig } from '../../config/config';
import { canEat, sizeAfterEating, sizeToMass } from '../formulas';
import { distance } from '../math/vec2';
import { SpatialHash } from '../spatialHash';
import type { Entity, EntityId } from '../types';

export interface EatEvent {
  attackerId: EntityId;
  preyId: EntityId;
  preySize: number;
}

const scratch: Entity[] = [];

export function resolveEating(
  entities: Entity[],
  spatial: SpatialHash,
  cfg: GameConfig,
): EatEvent[] {
  const events: EatEvent[] = [];

  // Process larger entities first so the biggest predator wins contested prey.
  const order = entities.filter((e) => e.alive).sort((a, b) => b.size - a.size);

  for (const attacker of order) {
    if (!attacker.alive) continue;
    const neighbors = spatial.queryRadius(
      attacker.pos.x,
      attacker.pos.y,
      attacker.size,
      scratch,
    );
    for (const prey of neighbors) {
      if (prey === attacker || !prey.alive) continue;
      if (attacker.size <= prey.size) continue; // quick reject
      const d = distance(attacker.pos, prey.pos);
      if (!canEat(attacker.size, prey.size, d, cfg)) continue;

      attacker.size = Math.min(cfg.world.maxSize, sizeAfterEating(attacker.size, prey.size, cfg));
      // Track scoring stats (continuous factors that differentiate the board).
      attacker.massEaten += sizeToMass(prey.size) * cfg.eat.massConversion;
      attacker.kills += 1;
      if (attacker.size > attacker.peakSize) attacker.peakSize = attacker.size;
      prey.alive = false;
      events.push({ attackerId: attacker.id, preyId: prey.id, preySize: prey.size });
    }
  }

  return events;
}
